/* Converto : store locator natif.
   Données : tableau JSON rendu par Liquid (métaobjets géolocalisés).
   Géocodage : api-adresse.data.gouv.fr (gratuit, sans clé, France).
   Carte : Mapbox GL chargé à la demande, uniquement si un jeton public est
   fourni en réglage de section. Sans jeton, la liste seule fonctionne. */
(() => {
  'use strict';
  if (customElements.get('store-locator')) return;

  const GEOCODE_URL = 'https://api-adresse.data.gouv.fr/search/';
  const MAPBOX_VERSION = '3.6.0';
  const MAPBOX_BASE = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl`;
  const EARTH_RADIUS_KM = 6371;

  const toRad = (deg) => (deg * Math.PI) / 180;
  const haversine = (a, b) => {
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2
      + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
  };
  const toNumber = (value) => {
    const n = parseFloat(String(value ?? '').replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };
  const formatKm = (km) => {
    const rounded = km < 10 ? Math.round(km * 10) / 10 : Math.round(km);
    return rounded.toLocaleString(document.documentElement.lang || 'fr');
  };

  let mapboxLoading = null;
  const loadMapbox = () => {
    if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
    if (mapboxLoading) return mapboxLoading;
    mapboxLoading = new Promise((resolve, reject) => {
      if (!document.querySelector('link[data-sl-mapbox-css]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `${MAPBOX_BASE}.css`;
        link.dataset.slMapboxCss = '';
        document.head.appendChild(link);
      }
      const script = document.createElement('script');
      script.src = `${MAPBOX_BASE}.js`;
      script.async = true;
      script.onload = () => (window.mapboxgl ? resolve(window.mapboxgl) : reject(new Error('mapbox')));
      script.onerror = () => reject(new Error('mapbox'));
      document.head.appendChild(script);
    });
    return mapboxLoading;
  };

  class StoreLocator extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-sl-form]');
      this.input = this.querySelector('[data-sl-query]');
      this.radius = this.querySelector('[data-sl-radius]');
      this.status = this.querySelector('[data-sl-status]');
      this.list = this.querySelector('[data-sl-list]');
      this.mapEl = this.querySelector('[data-sl-map]');
      this.geolocBtn = this.querySelector('[data-sl-geoloc]');
      this.sortEl = this.querySelector('[data-sl-sort]');
      this.submitBtn = this.form?.querySelector('[type="submit"]');
      this.stores = this.readStores();
      this.markers = [];
      this.abort = null;

      if (this.radius && this.dataset.defaultRadius != null) this.radius.value = this.dataset.defaultRadius;

      this._onSubmit = (e) => { e.preventDefault(); this.search(this.input?.value || ''); };
      this._onRadius = () => { if (this.center) this.render(); };
      this._onListClick = (e) => this.onListClick(e);
      this._onViewClick = (e) => { const b = e.target.closest('[data-sl-view]'); if (b) this.setView(b.dataset.slView); };
      this.addEventListener('click', this._onViewClick);
      this._onGeoloc = () => this.locate();
      this.form?.addEventListener('submit', this._onSubmit);
      this.radius?.addEventListener('change', this._onRadius);
      this.list?.addEventListener('click', this._onListClick);
      if (this.geolocBtn && 'geolocation' in navigator && window.isSecureContext) {
        this.geolocBtn.hidden = false;
        this.geolocBtn.addEventListener('click', this._onGeoloc);
      }

      const params = new URLSearchParams(window.location.search);
      const prefill = (params.get('zip') || params.get('q') || '').trim();
      if (prefill && this.input) {
        this.input.value = prefill;
        this.search(prefill);
      } else if (this.dataset.showAll === 'true') {
        this.renderAll();
      } else {
        this.setStatus(this.text('all', this.stores.length));
      }
      if (this.mapEl && this.dataset.mapboxToken) this.initMap();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.form?.removeEventListener('submit', this._onSubmit);
      this.radius?.removeEventListener('change', this._onRadius);
      this.list?.removeEventListener('click', this._onListClick);
      this.removeEventListener('click', this._onViewClick);
      this.geolocBtn?.removeEventListener('click', this._onGeoloc);
      this.map?.remove?.();
      this.map = null;
    }

    /* ---------- Données ---------- */
    readStores() {
      const script = this.querySelector('[data-sl-stores]');
      let raw = [];
      try { raw = JSON.parse(script?.textContent || '[]'); } catch (e) { raw = []; }
      // Coordonnées hors plage (point décimal perdu à la saisie, champs
      // inversés) : le point est ignoré plutôt que de faire planter la carte.
      const valid = (store) => store.lat !== null && store.lng !== null
        && Math.abs(store.lat) <= 90 && Math.abs(store.lng) <= 180;
      const stores = raw.map((store, index) => ({ ...store, index, lat: toNumber(store.lat), lng: toNumber(store.lng) }));
      const rejected = stores.filter((store) => !valid(store));
      if (rejected.length && window.Shopify?.designMode) {
        console.warn('[store-locator] coordonnées invalides ignorées :', rejected.map((s) => `${s.name} (${s.lat}, ${s.lng})`).join(' ; '));
      }
      return stores.filter(valid);
    }

    text(key, value) {
      const map = {
        results: this.dataset.textResults,
        resultOne: this.dataset.textResultOne,
        all: this.dataset.textAll,
        noResults: this.dataset.textNoResults,
        geocodeError: this.dataset.textGeocodeError,
        networkError: this.dataset.textNetworkError,
        geolocError: this.dataset.textGeolocError,
        distance: this.dataset.textDistance,
        call: this.dataset.textCall,
        website: this.dataset.textWebsite,
        directions: this.dataset.textDirections,
        around: this.dataset.textAround,
        aroundMe: this.dataset.textAroundMe,
      };
      // Les textes traduits arrivent parfois avec des entités HTML (&#39;) :
      // on les décode une fois, hors du DOM, avant insertion en textContent.
      if (!this._decoded) this._decoded = {};
      if (!(key in this._decoded)) {
        const box = document.createElement('textarea');
        box.innerHTML = map[key] || '';
        this._decoded[key] = box.value;
      }
      const tpl = this._decoded[key];
      return value == null ? tpl : tpl.replace('999999', String(value));
    }

    setStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.classList.toggle('store-locator__status--error', Boolean(isError));
    }

    setBusy(busy) {
      this.submitBtn?.classList.toggle('is-loading', busy);
      this.submitBtn?.toggleAttribute('disabled', busy);
      this.list?.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    /* ---------- Recherche ---------- */
    async search(query) {
      const q = String(query || '').trim();
      if (!q) { this.input?.focus(); return; }
      this.abort?.abort();
      const abort = new AbortController();
      this.abort = abort;
      this.setBusy(true);
      this.setStatus('');
      try {
        const center = await this.geocode(q, abort.signal);
        if (abort.signal.aborted || !this.isConnected) return;
        if (!center) { this.setStatus(this.text('geocodeError'), true); return; }
        this.center = center;
        this.render();
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.setStatus(this.text('networkError'), true);
      } finally {
        if (this.abort === abort) this.setBusy(false);
      }
    }

    async geocode(query, signal) {
      const params = new URLSearchParams({ q: query, limit: '1', autocomplete: '0' });
      const isZip = /^\d{5}$/.test(query);
      if (isZip) { params.set('type', 'municipality'); params.set('postcode', query); }
      let res = await fetch(`${GEOCODE_URL}?${params}`, { signal });
      if (!res.ok) throw new Error('geocode');
      let data = await res.json();
      let feature = data.features?.[0];
      if (!feature && isZip) {
        // Code postal sans commune homonyme : recherche libre en repli.
        res = await fetch(`${GEOCODE_URL}?${new URLSearchParams({ q: query, limit: '1' })}`, { signal });
        if (!res.ok) throw new Error('geocode');
        data = await res.json();
        feature = data.features?.[0];
      }
      if (!feature) return null;
      const [lng, lat] = feature.geometry.coordinates;
      return { lat, lng, label: feature.properties?.label || query, city: feature.properties?.city || feature.properties?.name || query };
    }

    locate() {
      if (!('geolocation' in navigator)) return;
      this.abort?.abort();
      this.setBusy(true);
      this.setStatus('');
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!this.isConnected) return;
          this.center = { lat: pos.coords.latitude, lng: pos.coords.longitude, label: '', city: '', me: true };
          if (this.input) this.input.value = '';
          this.setBusy(false);
          this.render();
        },
        () => { this.setBusy(false); this.setStatus(this.text('geolocError'), true); },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }

    /* ---------- Rendu ---------- */
    render() {
      if (!this.center) return;
      const radius = Number(this.radius?.value || 0);
      const limit = Number(this.dataset.maxResults) || 30;
      const results = this.stores
        .map((store) => ({ ...store, distance: haversine(this.center, store) }))
        .filter((store) => !radius || store.distance <= radius)
        .sort((a, b) => a.distance - b.distance)
        .slice(0, limit);
      this.results = results;
      this.renderList(results, true);
      if (results.length === 0) this.setStatus(this.text('noResults'), true);
      else if (results.length === 1) this.setStatus(this.text('resultOne'));
      else this.setStatus(this.text('results', results.length));
      if (this.sortEl) this.sortEl.hidden = results.length < 2;
      this.setChip(this.center.me ? this.text('aroundMe') : this.text('around', this.center.city || this.center.label));
      this.updateMap(results, true);
      if (results.length) this.select(results[0], { fly: false });
      else this.clearSelection();
    }

    renderAll() {
      const sorted = [...this.stores].sort((a, b) => (a.zip || '').localeCompare(b.zip || '') || (a.city || '').localeCompare(b.city || ''));
      this.results = sorted;
      this.renderList(sorted, false);
      this.setStatus(this.text('all', sorted.length));
      if (this.sortEl) this.sortEl.hidden = true;
      this.setChip('');
      this.clearSelection();
      this.updateMap(sorted, false);
    }

    renderList(stores, withDistance) {
      if (!this.list) return;
      const items = stores.map((store) => this.renderItem(store, withDistance));
      this.list.replaceChildren(...items);
    }

    icon(name) {
      if (!this._icons) {
        this._icons = {};
        const tpl = this.querySelector('[data-sl-icons]');
        tpl?.content.querySelectorAll('[data-icon]').forEach((el) => { this._icons[el.dataset.icon] = el.firstElementChild; });
      }
      const svg = this._icons[name];
      return svg ? svg.cloneNode(true) : null;
    }

    action(className, href, label, iconName, { external = false, ariaLabel = '' } = {}) {
      const a = document.createElement('a');
      a.className = `store-locator__action${className ? ` ${className}` : ''}`;
      a.href = href;
      if (external) { a.target = '_blank'; a.rel = 'noopener'; }
      if (ariaLabel) a.setAttribute('aria-label', ariaLabel);
      const icon = this.icon(iconName);
      if (icon) a.appendChild(icon);
      const span = document.createElement('span');
      span.textContent = label;
      a.appendChild(span);
      return a;
    }

    /* Rangée Itinéraire / Appeler / Site : partagée par la carte de la liste et
       le panneau « point sélectionné » sous la carte. */
    actionsRow(store, className = '') {
      const actions = document.createElement('div');
      actions.className = `store-locator__actions-row${className ? ` ${className}` : ''}`;
      actions.appendChild(this.action(
        'store-locator__action--primary',
        `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`,
        this.text('directions'), 'directions', { external: true }
      ));
      if (store.phone) {
        actions.appendChild(this.action(
          '', `tel:${store.phone.replace(/[^\d+]/g, '')}`, this.text('call'), 'phone',
          { ariaLabel: `${this.text('call')} ${store.phone}` }
        ));
      }
      if (store.url) {
        actions.appendChild(this.action(
          '', /^https?:\/\//i.test(store.url) ? store.url : `https://${store.url}`,
          this.text('website'), 'external', { external: true }
        ));
      }
      return actions;
    }

    renderItem(store, withDistance) {
      const li = document.createElement('li');
      li.className = 'store-locator__item';
      li.dataset.slIndex = String(store.index);

      const body = document.createElement('div');
      body.className = 'store-locator__body';

      const head = document.createElement('div');
      head.className = 'store-locator__item-head';
      const name = document.createElement('h3');
      name.className = 'store-locator__name';
      name.textContent = store.name || '';
      head.appendChild(name);
      if (withDistance && Number.isFinite(store.distance)) {
        const dist = document.createElement('span');
        dist.className = 'store-locator__distance';
        dist.textContent = this.text('distance', formatKm(store.distance));
        head.appendChild(dist);
      }
      body.appendChild(head);

      const address = document.createElement('p');
      address.className = 'store-locator__address';
      const cityLine = [store.zip, store.city].filter(Boolean).join(' ');
      if (store.address) {
        const street = document.createElement('span');
        street.className = 'store-locator__street';
        street.textContent = store.address;
        address.appendChild(street);
      }
      if (cityLine) {
        const city = document.createElement('span');
        city.className = 'store-locator__city';
        city.textContent = cityLine;
        address.appendChild(city);
      }
      body.appendChild(address);

      const actions = this.actionsRow(store);
      body.appendChild(actions);
      li.appendChild(body);
      return li;
    }

    onListClick(e) {
      if (e.target.closest('a')) return;
      const item = e.target.closest('[data-sl-index]');
      if (!item) return;
      const store = this.stores.find((s) => String(s.index) === item.dataset.slIndex);
      if (store) this.select(store, { fly: true });
    }

    /* Sélection d'un point : carte active dans la liste, panneau sous la
       carte, vol vers le point et ouverture de sa bulle si demandé. */
    select(store, { fly = false, fromMap = false } = {}) {
      this.selected = store;
      this.list?.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
      const item = this.list?.querySelector(`[data-sl-index="${store.index}"]`);
      item?.classList.add('is-active');
      if (fromMap && item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      const panel = this.querySelector('[data-sl-selected]');
      if (panel) {
        panel.hidden = false;
        panel.querySelector('[data-sl-selected-name]').textContent = store.name || '';
        const cityLine = [store.zip, store.city].filter(Boolean).join(' ');
        panel.querySelector('[data-sl-selected-address]').textContent = [store.address, cityLine].filter(Boolean).join(' \u00b7 ');
        const slot = panel.querySelector('[data-sl-selected-actions]');
        if (slot) slot.replaceChildren(...this.actionsRow(store).childNodes);
      }
      if (fly && this.map && this.mapReady) {
        this.map.flyTo({ center: [store.lng, store.lat], zoom: 14, essential: true });
        // Le marqueur n'existe qu'une fois le point sorti de son groupe : on
        // attend la fin du déplacement pour ouvrir sa bulle.
        this.map.once('moveend', () => {
          this.refreshMarkers();
          const marker = this.markerMap?.[store.index];
          if (marker && !marker.getPopup().isOpen()) marker.togglePopup();
        });
      }
    }

    clearSelection() {
      this.selected = null;
      this.list?.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
      const panel = this.querySelector('[data-sl-selected]');
      if (panel) panel.hidden = true;
    }

    setChip(text) {
      const chip = this.querySelector('[data-sl-map-chip]');
      if (!chip) return;
      chip.hidden = !text;
      const label = chip.querySelector('[data-sl-map-chip-text]');
      if (label) label.textContent = text || '';
    }

    /* Bascule Liste / Carte (mobile) : la carte, masquée au chargement, doit
       être redimensionnée et recadrée une fois visible. */
    setView(view) {
      const showMap = view === 'map';
      this.classList.toggle('store-locator--view-map', showMap);
      this.querySelectorAll('[data-sl-view]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.slView === view)));
      if (showMap && this.map && this.mapReady) {
        requestAnimationFrame(() => {
          this.map.resize();
          this.updateMap(this.results || this.stores, Boolean(this.center));
        });
      }
    }

    /* ---------- Carte Mapbox (facultative) ---------- */
    async initMap() {
      if (!this.mapEl || this.map || this.mapInit) return;
      this.mapInit = true;
      try {
        const mapboxgl = await loadMapbox();
        if (!this.isConnected) return;
        mapboxgl.accessToken = this.dataset.mapboxToken;
        this.map = new mapboxgl.Map({
          container: this.mapEl,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [2.35, 46.6],
          zoom: 4.8,
          projection: 'mercator',
          cooperativeGestures: true,
          attributionControl: true,
        });
        this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
        this.map.on('load', () => {
          this.setupSource();
          this.mapReady = true;
          this.updateMap(this.results || this.stores, Boolean(this.center));
        });
      } catch (e) {
        this.mapEl.hidden = true;
        this.classList.remove('store-locator--map');
      }
    }

    /* Couleur réelle des marqueurs (variable CSS résolue), pour les calques
       Mapbox qui n'acceptent pas var(). */
    markerColor() {
      if (this._markerColor) return this._markerColor;
      const probe = document.createElement('span');
      probe.className = 'store-locator__marker';
      probe.style.position = 'absolute';
      probe.style.visibility = 'hidden';
      this.appendChild(probe);
      const color = getComputedStyle(probe).color || '#333';
      probe.remove();
      this._markerColor = color;
      const rgb = color.match(/\d+(\.\d+)?/g)?.map(Number) || [51, 51, 51];
      const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
      this._clusterText = luminance > 0.62 ? '#1a1a1a' : '#ffffff';
      return color;
    }

    /* Source GeoJSON regroupée : cercles + compteur pour les groupes, marqueurs
       DOM stylés (épingle, pastille, goutte) pour les points isolés. */
    setupSource() {
      const cluster = this.dataset.cluster !== 'false';
      this.map.addSource('sl-stores', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster,
        clusterMaxZoom: 13,
        clusterRadius: 48,
      });
      if (cluster) {
        const color = this.markerColor();
        this.map.addLayer({
          id: 'sl-clusters',
          type: 'circle',
          source: 'sl-stores',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': color,
            'circle-opacity': 0.92,
            'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 30, 25],
            'circle-stroke-width': 3,
            'circle-stroke-color': '#ffffff',
          },
        });
        this.map.addLayer({
          id: 'sl-cluster-count',
          type: 'symbol',
          source: 'sl-stores',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 13,
            'text-allow-overlap': true,
          },
          paint: { 'text-color': this._clusterText },
        });
        this.map.on('click', 'sl-clusters', (e) => {
          const feature = this.map.queryRenderedFeatures(e.point, { layers: ['sl-clusters'] })[0];
          if (!feature) return;
          this.map.getSource('sl-stores').getClusterExpansionZoom(feature.properties.cluster_id, (err, zoom) => {
            if (err) return;
            this.map.easeTo({ center: feature.geometry.coordinates, zoom: zoom + 0.3, duration: 500 });
          });
        });
        this.map.on('mouseenter', 'sl-clusters', () => { this.map.getCanvas().style.cursor = 'pointer'; });
        this.map.on('mouseleave', 'sl-clusters', () => { this.map.getCanvas().style.cursor = ''; });
      }
      this.markerMap = {};
      this._onRender = () => { if (this.map?.isSourceLoaded('sl-stores')) this.refreshMarkers(); };
      this.map.on('render', this._onRender);
    }

    createMarker(store) {
      const popup = new window.mapboxgl.Popup({ offset: 24, closeButton: false, maxWidth: '260px' });
      const box = document.createElement('div');
      box.className = 'store-locator__popup';
      const strong = document.createElement('strong');
      strong.textContent = store.name || '';
      box.appendChild(strong);
      const addr = document.createElement('div');
      addr.textContent = [store.address, [store.zip, store.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
      box.appendChild(addr);
      popup.setDOMContent(box);
      const style = this.dataset.markerStyle || 'pin';
      const el = document.createElement('button');
      el.type = 'button';
      el.className = `store-locator__marker store-locator__marker--${style}`;
      el.setAttribute('aria-label', store.name || '');
      el.addEventListener('click', () => this.select(store, { fly: false, fromMap: true }));
      if (style === 'pin') {
        const shape = this.icon('marker');
        if (shape) el.appendChild(shape);
      }
      const marker = new window.mapboxgl.Marker({ element: el, anchor: style === 'dot' ? 'center' : 'bottom' })
        .setLngLat([store.lng, store.lat])
        .setPopup(popup);
      marker._slIndex = store.index;
      return marker;
    }

    /* Synchronise les marqueurs DOM avec les points actuellement isolés
       (hors groupe) dans la vue : création à l'apparition, retrait sinon. */
    refreshMarkers() {
      if (!this.map || !this.storeByIndex) return;
      const visible = new Set();
      this.map.querySourceFeatures('sl-stores').forEach((feature) => {
        if (feature.properties.point_count) return;
        const index = feature.properties.index;
        visible.add(index);
        if (this.markerMap[index]) return;
        const store = this.storeByIndex.get(index);
        if (!store) return;
        try {
          this.markerMap[index] = this.createMarker(store).addTo(this.map);
        } catch (e) { /* point ignoré */ }
      });
      Object.keys(this.markerMap).forEach((key) => {
        const index = Number(key);
        if (visible.has(index)) return;
        this.markerMap[index].remove();
        delete this.markerMap[index];
      });
      this.markers = Object.values(this.markerMap);
    }

    updateMap(stores, fitToResults) {
      // Pas map.loaded() : il vaut false pendant une animation (fitBounds),
      // ce qui ferait sauter le rafraîchissement des marqueurs.
      if (!this.map || !window.mapboxgl || !this.mapReady) return;
      Object.values(this.markerMap || {}).forEach((m) => m.remove());
      this.markerMap = {};
      this.markers = [];
      this.storeByIndex = new Map(stores.map((store) => [store.index, store]));
      const features = stores.map((store) => ({
        type: 'Feature',
        id: store.index,
        geometry: { type: 'Point', coordinates: [store.lng, store.lat] },
        properties: { index: store.index },
      }));
      this.map.getSource('sl-stores')?.setData({ type: 'FeatureCollection', features });
      if (!stores.length) return;
      const bounds = new window.mapboxgl.LngLatBounds();
      stores.forEach((store) => bounds.extend([store.lng, store.lat]));
      if (fitToResults && this.center) {
        bounds.extend([this.center.lng, this.center.lat]);
        this.map.fitBounds(bounds, { padding: 48, maxZoom: 13, duration: 600 });
      } else {
        this.map.fitBounds(this.mainBounds(stores) || bounds, { padding: 40, maxZoom: 11, duration: 0 });
      }
    }

    /* Cadrage initial : le groupe principal de points (autour de leur centre
       médian, rayon 1 500 km), pour qu'un point outre-mer ou étranger
       n'écrase pas la carte sur un globe entier. */
    mainBounds(stores) {
      if (stores.length < 2) return null;
      const median = (values) => {
        const sorted = [...values].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
      };
      const center = { lat: median(stores.map((s) => s.lat)), lng: median(stores.map((s) => s.lng)) };
      const near = stores.filter((store) => haversine(center, store) <= 1500);
      if (!near.length) return null;
      const bounds = new window.mapboxgl.LngLatBounds();
      near.forEach((store) => bounds.extend([store.lng, store.lat]));
      return bounds;
    }
  }

  customElements.define('store-locator', StoreLocator);
})();
